/**
 * Thin chat-api client. The only job here is to turn the wire-level
 * outcome (HTTP status, network failure, abort) into a typed `ApiError`
 * with a categorical `kind` that the UI maps to localized copy.
 *
 * No retries — rate-limit responses bubble up as-is so the user sees a
 * "wait a moment" message rather than us hammering the endpoint.
 */

import type { ChatRequest, ChatResponse } from './types';
import type { ErrorKind } from './i18n';

const DEFAULT_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
  constructor(
    public readonly kind: ErrorKind,
    public readonly status: number | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Bucket an HTTP status into the kinds we localize. Returns null for 2xx,
 * 'generic' for anything outside the documented chat-api response codes.
 */
export function classifyStatus(status: number): ErrorKind | null {
  if (status >= 200 && status < 300) return null;
  if (status === 429) return 'rateLimit';
  if (status >= 500) return 'serverError';
  if (status >= 400) return 'badRequest';
  return 'generic';
}

export interface PostChatDeps {
  /** Inject for tests. Defaults to `globalThis.fetch`. */
  fetch?: typeof fetch;
  /** Per-request timeout in ms. Defaults to 30s. */
  timeoutMs?: number;
  /** Optional caller-provided abort signal (e.g. user-cancel button). */
  signal?: AbortSignal;
}

export async function postChat(
  url: string,
  request: ChatRequest,
  deps: PostChatDeps = {},
): Promise<ChatResponse> {
  const fetchImpl = deps.fetch ?? fetch;
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);

  // Forward an external abort signal into the same controller so callers can
  // cancel via either mechanism.
  if (deps.signal) {
    if (deps.signal.aborted) controller.abort(deps.signal.reason);
    else deps.signal.addEventListener('abort', () => controller.abort(deps.signal!.reason), { once: true });
  }

  try {
    const res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    const kind = classifyStatus(res.status);
    if (kind !== null) {
      const body = await res.text().catch(() => '');
      throw new ApiError(kind, res.status, body || `HTTP ${res.status}`);
    }
    return (await res.json()) as ChatResponse;
  } catch (err) {
    if (err instanceof ApiError) throw err;

    // Distinguish timeout from caller-cancel from generic abort. Modern
    // fetch sets DOMException with name 'TimeoutError' when AbortSignal.timeout
    // is used; we set the same name when our timer fires.
    if (err instanceof DOMException) {
      if (err.name === 'TimeoutError') {
        throw new ApiError('timeout', null, 'Request timed out');
      }
      if (err.name === 'AbortError') {
        // Caller cancelled — bubble up generic; UI typically suppresses this.
        throw new ApiError('generic', null, 'Aborted');
      }
    }

    // `fetch` rejects with TypeError on network-layer failures (offline, DNS,
    // CORS preflight, etc.). Worth distinguishing from server errors.
    if (err instanceof TypeError) {
      throw new ApiError('network', null, err.message);
    }

    throw new ApiError('generic', null, err instanceof Error ? err.message : String(err));
  } finally {
    clearTimeout(timer);
  }
}
